import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadUser } from '../ducks/Auth/actions';
import { setNotificationPromptCallback } from '../utils/notificationUtils';

export const useNotificationPrompt = () => {
  const dispatch = useDispatch();
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptContext, setPromptContext] = useState('general');
  const auth = useSelector((state) => state.auth);
  const user = useSelector((state) => state.auth.user);

  // True if the user has either dismissed the prompt locally OR already made a
  // server-side decision (token saved or explicitly null). Property-existence
  // check, NOT a truthy check — `fcmToken: null` still counts as "decided".
  const hasDismissedPrompt =
    localStorage.getItem('notificationPromptDismissed') === 'true';
  const hasMadeServerDecision =
    !!user && Object.prototype.hasOwnProperty.call(user, 'fcmToken');
  const shouldSuppress = hasDismissedPrompt || hasMadeServerDecision;

  useEffect(() => {
    const handleGlobalPrompt = (context) => {
      if (!shouldSuppress) {
        setPromptContext(context);
        setShowPrompt(true);
      }
    };
    setNotificationPromptCallback(handleGlobalPrompt);
  }, [shouldSuppress]);

  // First-time auto-prompt after login
  useEffect(() => {
    if (auth.isAuthenticated && user && !shouldSuppress && !showPrompt) {
      const timer = setTimeout(() => {
        setPromptContext('firstLogin');
        setShowPrompt(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [auth.isAuthenticated, user, shouldSuppress, showPrompt]);

  const handlePromptClose = (enabled) => {
    setShowPrompt(false);
    // Always persist the dismissal locally so the prompt never fires twice.
    localStorage.setItem('notificationPromptDismissed', 'true');
    if (enabled) {
      // Refresh redux user so fcmToken populates without a full page reload.
      dispatch(loadUser());
    }
  };

  return {
    showPrompt,
    promptContext,
    handlePromptClose,
  };
};
